import { useMemo, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { publicApplications, type PublicApplicationRequest } from "@/features/applications/api/applications.api";
import { toApiDateTime } from "@/utils/dates";
import { Form } from "@/components/ui/form";
import { FormFieldSize, FormRequiredCue } from "@/components/form";
import { Loading } from "@/components/Loading";
import { useConfirm } from "@/components/ConfirmDialog";
import { loadFormDraft, useFormDraftAutosave } from "@/hooks/useFormDraftAutosave";
import {
  Progress, StickyActions, useWizardSteps, useShareLink, fileBase64, clean, num,
  ConversionErrorBanner, conversionMessage, failedDocSlots, requiredDocSlots, REUPLOAD_HINT, type DocSlot,
  DraftSaveIndicator, DraftRestoredNotice, onboardingDraftKey, previousFileNote,
  DRAFT_SENSITIVE_FIELDS, type DraftFiles, canonicalOwnershipType,
} from "@/features/onboarding/components/wizard";
import {
  STEPS, schema, type FormState, STEP_FIELDS, EMPTY_FORM, documentName,
  WelcomeStep, LegalStep, OwnerStep, BankStep,
} from "@/features/onboarding/components/verify";

const DL_STEP = 2;
const CHECK_STEP = 3;
const MISSING_DL = "Please add a photo of your driver's license.";
const MISSING_CHECK = "Please add a photo of a voided business check.";

export default function BusinessVerificationPage() {
  const { ref } = useParams<{ ref?: string }>();

  const [searchParams] = useSearchParams();

  const referringRef = ref?.trim() || null;
  const shareLink = useShareLink(referringRef, "VERIFICATION", searchParams.get("c")?.trim() || null);
  const confirm = useConfirm();
  const draftKey = onboardingDraftKey("verify", referringRef);
  const [initialDraft] = useState(() => loadFormDraft(draftKey, EMPTY_FORM));
  const [restoredAt, setRestoredAt] = useState<number | null>(initialDraft?.draft.lastEditAt ?? null);
  const [previousFiles, setPreviousFiles] = useState<DraftFiles>(initialDraft?.draft.files ?? {});

  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [dlFile, setDlFile] = useState<File | null>(null);
  const [checkFile, setCheckFile] = useState<File | null>(null);
  const [failedDocs, setFailedDocs] = useState<DocSlot[]>([]);
  const [failAttempt, setFailAttempt] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [missingDoc, setMissingDoc] = useState<DocSlot | null>(null);

  const form = useForm<FormState>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: initialDraft
      ? { ...initialDraft.values, ownershipType: canonicalOwnershipType(initialDraft.values.ownershipType) }
      : EMPTY_FORM,
  });

  const wiz = useWizardSteps({
    form,
    stepCount: STEPS.length,
    stepFields: STEP_FIELDS,
    minStep: 1,
    initialStep: initialDraft?.draft.step,
    initialMaxValidated: initialDraft?.draft.maxValidated,
  });

  const draftFiles = useMemo<DraftFiles>(() => ({
    driverLicense: dlFile?.name ?? previousFiles.driverLicense,
    voidedCheck: checkFile?.name ?? previousFiles.voidedCheck,
  }), [dlFile, checkFile, previousFiles]);

  const autosave = useFormDraftAutosave({
    form,
    storageKey: draftKey,
    enabled: !submitted,
    restored: initialDraft?.draft ?? null,
    step: wiz.step,
    maxValidated: wiz.maxValidated,
    files: draftFiles,
    sensitive: DRAFT_SENSITIVE_FIELDS,
  });

  function attachDl(f: File | null) {
    setDlFile(f);
    setPreviousFiles((p) => ({ ...p, driverLicense: undefined }));
  }
  function attachCheck(f: File | null) {
    setCheckFile(f);
    setPreviousFiles((p) => ({ ...p, voidedCheck: undefined }));
  }

  async function startOver() {
    const ok = await confirm({
      title: "Start over?",
      description: "Everything you've entered on this device will be cleared. This can't be undone.",
      confirmLabel: "Start over",
      destructive: true,
    });
    if (!ok) return;
    autosave.discard();
    setRestoredAt(null);
    setPreviousFiles({});
    setFailedDocs([]);
    setMissingDoc(null);
    setError(null);
    setDlFile(null);
    setCheckFile(null);
    form.reset(EMPTY_FORM);
    wiz.reset();
  }

  function flagMissingDoc(): DocSlot | null {
    const missing: DocSlot | null = !dlFile
      ? "driverLicense"
      : !checkFile
        ? "voidedCheck"
        : null;
    setMissingDoc(missing);
    return missing;
  }

  async function handleNext() {
    autosave.flush();
    if (wiz.step === DL_STEP && !dlFile) {
      setMissingDoc("driverLicense");
      return;
    }
    setMissingDoc(null);
    if (await wiz.next()) setError(null);
  }
  function handleBack() {
    setError(null);
    wiz.back();
  }
  function handleGoTo(target: number) {
    setError(null);
    wiz.goToStep(target);
  }

  const submit = form.handleSubmit(
    async (v) => {
      const missing = flagMissingDoc();
      if (missing) {
        if (missing === "driverLicense") wiz.setStep(DL_STEP);
        return;
      }
      setError(null);
      setFailedDocs([]);
      try {
        const dba = v.dbaSameAsLegal ? v.legalName.trim() : v.dbaName.trim();
        const payload: PublicApplicationRequest = {
          dbaName: dba,
          legalName: clean(v.legalName),
          mcc: clean(v.mcc),
          contactName: clean(v.contactName),
          email: clean(v.email),
          phone: clean(v.phone),
          addressLine1: clean(v.addressLine1),
          addressLine2: clean(v.addressLine2),
          city: clean(v.city),
          state: clean(v.state),
          zip: clean(v.zip),
          ein: clean(v.ein),
          agentCode: referringRef,
          campaignId: shareLink.campaignId,
          website: clean(v.website),
          ownershipType: clean(v.ownershipType),
          businessStartDate: toApiDateTime(v.businessStartDate),
          dbaSameAsLegal: v.dbaSameAsLegal,
          dbaAddressLine1: clean(v.dbaAddressLine1),
          dbaCity: clean(v.dbaCity),
          dbaState: clean(v.dbaState),
          dbaZip: clean(v.dbaZip),
          averageMonthlyVolume: num(v.averageMonthlyVolume),
          averageTransactionAmount: num(v.averageTransactionAmount),
          maxTransactionAmount: num(v.maxTransactionAmount),
          productDescription: clean(v.productDescription),
          ownerFirstName: clean(v.ownerFirstName),
          ownerMiddleName: clean(v.ownerMiddleName),
          ownerLastName: clean(v.ownerLastName),
          ownerDateOfBirth: toApiDateTime(v.ownerDateOfBirth),
          ownerIsUsCitizen: v.ownerCitizenship === "us",
          ownerSsn: clean(v.ownerSsn),
          ownerPhone: clean(v.ownerPhone),
          ownerHomeAddressLine1: clean(v.ownerHomeAddressLine1),
          ownerHomeCity: clean(v.ownerHomeCity),
          ownerHomeState: clean(v.ownerHomeState),
          ownerHomeZip: clean(v.ownerHomeZip),
          bankName: clean(v.bankName),
          depositAccountType: clean(v.depositAccountType),
          ssn: clean(v.ssn),
          bankRoutingNumber: clean(v.bankRoutingNumber),
          bankAccountNumber: clean(v.bankAccountNumber),
          separateWithdrawAccount: v.separateWithdrawAccount,
          withdrawBankName: clean(v.withdrawBankName),
          withdrawAccountType: clean(v.withdrawAccountType),
          withdrawRoutingNumber: v.separateWithdrawAccount ? clean(v.withdrawRoutingNumber) : null,
          withdrawAccountNumber: v.separateWithdrawAccount ? clean(v.withdrawAccountNumber) : null,
          driverLicenseImageBase64: dlFile ? await fileBase64(dlFile) : null,
          driverLicenseFileName: documentName(dlFile, `${v.ownerFirstName}_DriverLicense`),
          driverLicenseContentType: dlFile?.type || null,
          voidedCheckImageBase64: checkFile ? await fileBase64(checkFile) : null,
          voidedCheckFileName: documentName(checkFile, `${dba}_void`),
          voidedCheckContentType: checkFile?.type || null,
        };
        await publicApplications.submitVerification(payload);
        autosave.discard();
        setRestoredAt(null);
        setPreviousFiles({});
        setSubmitted(true);
      } catch (e2) {
        const slots = failedDocSlots(e2);
        if (slots.length > 0) {
          setFailedDocs(slots);
          setFailAttempt((n) => n + 1);
          if (slots.includes("driverLicense")) setDlFile(null);
          if (slots.includes("voidedCheck")) setCheckFile(null);
          return;
        }
        const missing = requiredDocSlots(e2);
        if (missing.length > 0) {
          if (missing.includes("driverLicense")) setDlFile(null);
          if (missing.includes("voidedCheck")) setCheckFile(null);
          setMissingDoc(missing[0]);
          wiz.setStep(missing[0] === "driverLicense" ? DL_STEP : CHECK_STEP);
          return;
        }
        setError(e2 instanceof Error ? e2.message : "Something went wrong. Please try again.");
      }
    },
    (errs) => {
      setError(null);
      flagMissingDoc();
      wiz.goToError(errs);
    },
  );

  const { step, maxValidated } = wiz;
  const isLast = step === STEPS.length - 1;
  const isWelcome = step === 0;
  const dlError = dlFile
    ? null
    : failedDocs.includes("driverLicense")
      ? REUPLOAD_HINT
      : missingDoc === "driverLicense"
        ? MISSING_DL
        : null;
  const checkError = checkFile
    ? null
    : failedDocs.includes("voidedCheck")
      ? REUPLOAD_HINT
      : missingDoc === "voidedCheck"
        ? MISSING_CHECK
        : null;
  const conversionError = conversionMessage(failedDocs);

  function goToFailedDoc() {
    wiz.setStep(failedDocs.includes("driverLicense") ? DL_STEP : CHECK_STEP);
  }

  if (shareLink.checking) {
    return (
      <Shell>
        <div className="px-6 py-12 flex justify-center">
          <Loading>Checking your link…</Loading>
        </div>
      </Shell>
    );
  }

  if (shareLink.invalid) {
    return (
      <Shell>
        <div className="text-center py-12 px-6">
          <h1 className="text-xl font-semibold text-slate-800">Link not valid</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            This verification link is no longer valid. Please ask your BLogic representative
            for a new one.
          </p>
        </div>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <Confirmation />
      </Shell>
    );
  }

  return (
    <Form {...form}>
      <FormFieldSize value="lg">
      <FormRequiredCue>
      <div className="min-h-screen bg-white sm:bg-slate-50 flex flex-col">
        <Header />
        <Progress steps={STEPS} step={step} maxValidated={maxValidated} onStepClick={handleGoTo} disabled={scanning} />

        <main className={"flex-1 sm:px-4 sm:pt-4 " + (isWelcome ? "pb-8" : "pb-32 sm:pb-28")}>
          <div className="max-w-2xl mx-auto">
            {!isWelcome && (
              <div className="px-4 sm:px-0">
                <DraftSaveIndicator status={autosave.status} savedAt={autosave.savedAt} />
              </div>
            )}
            <div className="bg-white px-4 py-5 sm:rounded-2xl sm:border sm:border-slate-200 sm:p-7">
              {restoredAt != null && !isWelcome && (
                <DraftRestoredNotice at={restoredAt} onStartOver={startOver} disabled={scanning} />
              )}
              {conversionError && (
                <ConversionErrorBanner message={conversionError} attempt={failAttempt} onJump={goToFailedDoc} />
              )}
              {step === 0 && <WelcomeStep onStart={handleNext} />}
              {step === 1 && <LegalStep form={form} />}
              {step === 2 && (
                <OwnerStep
                  form={form}
                  dlFile={dlFile}
                  onDlFile={attachDl}
                  dlError={dlError}
                  dlPreviousNote={previousFileNote("driverLicense", previousFiles.driverLicense)}
                  onScanned={autosave.flush}
                  onBusyChange={setScanning}
                />
              )}
              {step === 3 && (
                <BankStep
                  form={form}
                  checkFile={checkFile}
                  onCheckFile={attachCheck}
                  checkError={checkError}
                  checkPreviousNote={previousFileNote("voidedCheck", previousFiles.voidedCheck)}
                  onScanned={autosave.flush}
                  onBusyChange={setScanning}
                />
              )}
            </div>
          </div>
        </main>

        {error && (
          <div
            className="fixed bottom-[72px] inset-x-0 px-4 pb-2"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)" }}
          >
            <div className="max-w-2xl mx-auto bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg px-4 py-2">
              {error}
            </div>
          </div>
        )}

        {!isWelcome && (
          <StickyActions
            isFirst={step <= 1}
            isLast={isLast}
            submitting={form.formState.isSubmitting}
            busy={scanning}
            submitLabel="Complete Business Verification"
            hideBackWhenFirst
            onPrev={handleBack}
            onNext={handleNext}
            onSubmit={submit}
          />
        )}
      </div>
      </FormRequiredCue>
      </FormFieldSize>
    </Form>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white sm:bg-slate-50 flex flex-col">
      <Header />
      <main className="flex-1 sm:px-4 sm:py-10">
        <div className="max-w-2xl mx-auto bg-white sm:rounded-2xl sm:border sm:border-slate-200">
          {children}
        </div>
      </main>
    </div>
  );
}

function Header() {
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center font-bold text-primary-foreground text-sm">
          BL
        </div>
        <div className="leading-tight">
          <div className="font-semibold text-slate-900 text-sm">BLogic Systems</div>
          <div className="text-[11px] text-slate-500">Merchant Verification</div>
        </div>
      </div>
    </header>
  );
}

function Confirmation() {
  return (
    <div className="text-center py-12 px-6">
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-semibold">
        ✓
      </div>
      <h1 className="text-xl font-semibold text-slate-800 mt-4">
        Thank You
      </h1>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        Your business verification has been successfully submitted.
        A BLogic representative will contact you if additional documentation
        or verification is required.
      </p>
    </div>
  );
}
